<template>
  <main class="terms-wrapper">
    <section class="terms-card">
      <header class="terms-card__header">
        <img class="terms-card__logo" :src="clientLogoURL" :alt="`${clientName || 'Client'} logo`">
        <span v-if="clientName && !hideClientName" class="terms-card__client-name">{{ clientName }}</span>
      </header>
      <div class="terms-card__accent" aria-hidden="true"></div>

      <div class="terms-card__content">
        <h1>Terms of Use Agreement</h1>
        <p class="terms-card__intro">Please review and accept this agreement before continuing.</p>

        <LoadingIndicator v-if="loadingTerms" message="Loading agreement…" />
        <template v-else>
          <div class="terms-content">
            <HTMLText v-if="latestTerms?.terms" :value="latestTerms.terms" />
            <template v-else>
              <p>The current Terms of Use Agreement is unavailable. Please return to sign in and try again.</p>
              <o-button class="terms-action terms-action--secondary" @click="returnToSignIn">
                Return to sign in
              </o-button>
            </template>
            <p v-if="latestTerms?.created_at" class="terms-updated"><i>Updated on {{ formatCultureDate(latestTerms.created_at) }}</i></p>
          </div>

          <template v-if="latestTerms?.id">
            <label class="terms-acceptance">
              <o-checkbox v-model="agreed">
                I have read and agree to this agreement set forth {{ clientName ? `by ${clientName}` : 'by the general contractor' }}.
              </o-checkbox>
            </label>

            <div class="terms-actions">
              <o-button
                class="terms-action terms-action--primary"
                :disabled="!agreed || submitting"
                :loading="submitting"
                @click="handleAcceptTerms"
              >
                I Agree
              </o-button>
            </div>
          </template>
        </template>
      </div>
    </section>

    <footer class="terms-footer">
      <a href="https://pipelinesuite.com/" target="_blank"><img src="https://assets.p-suite.io/powered_by_preconSuiteLogo.svg" width="200" alt="Powered by PreconSuite"></a>
      <a href="https://pipelinesuite.com/privacy-policy" target="_blank">Privacy</a>
      <a href="https://pipelinesuite.com/terms-of-service" target="_blank">Terms</a>
      <a href="https://pipelinesuite.com/contact/" target="_blank">Contact Us</a>
    </footer>
  </main>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import HTMLText from '@/components/HTMLText.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import Auth from '@/auth';
import {useClientStore} from '@/stores/ClientStore';
import { useCompanyStore } from '@/stores/CompanyStore';
import { NotificationError } from '@/internal';
import {formatCultureDate} from '@/use/formatCultureValue';

const companyStore = useCompanyStore();
const { company } = storeToRefs(companyStore);
const {client} = storeToRefs(useClientStore());
const agreed = ref(false);
const submitting = ref(false);
const loadingTerms = ref(true);

const clientName = computed(() => client.value?.name || company.value?.client?.name || '');
const clientLogoURL = computed(() => client.value?.logo_url
  || company.value?.client?.logo_url
  || 'https://assets.p-suite.io/preconsuite-logo.svg');
const hideClientName = computed(() => client.value?.hide_client_name_on_subsportal
  ?? company.value?.client?.hide_client_name_on_subsportal
  ?? false);
const latestTerms = computed(() => company.value?.client?.latest_terms);

onMounted(async () => {
  try {
    await companyStore.getCompany({
      params: {
        include: ['client.latestTerms', 'primaryContact', 'trades'],
      },
    });
  } catch (error: any) {
    if (error.errors) {
      NotificationError({}, error);
    } else {
      NotificationError({ message: error });
    }
  } finally {
    loadingTerms.value = false;
  }
});

function returnPath(): string {
  const storedPath = window.sessionStorage.getItem('redirectPath');
  window.sessionStorage.removeItem('redirectPath');

  if (!storedPath) {
    return '/';
  }

  let url: URL;

  try {
    url = new URL(storedPath, window.location.origin);
  } catch {
    return '/';
  }

  return url.origin === window.location.origin && url.pathname !== '/terms'
    ? `${url.pathname}${url.search}${url.hash}`
    : '/';
}

function returnToSignIn() {
  window.sessionStorage.removeItem('redirectPath');
  Auth.logout();
}

async function handleAcceptTerms() {
  if (!latestTerms.value?.id) {
    return;
  }

  submitting.value = true;

  try {
    await companyStore.termsAccepted({ term_id: latestTerms.value.id });
    await companyStore.getCompany({
      params: {
        include: ['client.latestTerms', 'primaryContact', 'trades'],
      },
    });

    window.location.replace(returnPath());
  } catch (error: any) {
    if (error.errors) {
      NotificationError({}, error);
    } else {
      NotificationError({ message: error });
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<style lang="scss" scoped>
.terms-wrapper {
  background: #ffffff;
  color: #252729;
  flex: 1;
  min-height: 100vh;
  padding: 2rem 0;
}

.terms-card {
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1.5rem rgb(0 0 0 / 10%);
  margin: 0 auto;
  max-width: 68.75rem;
  overflow: hidden;
  padding: 0;
  width: calc(100% - 2rem);
}

.terms-card__header {
  align-items: center;
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem 0.5rem;
}

.terms-card__logo {
  display: block;
  height: 40px;
  max-width: 250px;
  object-fit: contain;
  object-position: left center;
  width: auto;
}

.terms-card__client-name {
  font-size: 1.25rem;
  font-weight: 600;
}

.terms-card__accent {
  background: var(--theme-menu-background-color);
  border-top: 2px solid var(--theme-border-color);
  box-shadow: 0 2px 2px rgb(0 0 0 / 14%),
    0 3px 1px -2px rgb(0 0 0 / 12%),
    0 1px 5px rgb(0 0 0 / 10%);
  height: 31px;
}

.terms-card__content {
  margin: 0 auto;
  max-width: 53.75rem;
  padding: 2.5rem 2rem 2rem;

  h1 {
    font-size: 2rem;
    margin: 0;
  }
}

.terms-card__intro {
  color: #5b6065;
  margin: 0.5rem 0 1.5rem;
}

.terms-content {
  border: 1px solid #d9dcde;
  border-radius: 4px;
  line-height: 1.55;
  max-height: 22.5rem;
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
}

.terms-acceptance {
  background: #f7f8f8;
  border: 1px solid #d9dcde;
  border-radius: 4px;
  display: block;
  margin-top: 1.5rem;
  padding: 1rem 1.25rem;
}

.terms-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.25rem;
}

.terms-action {
  align-items: center;
  border-radius: 4px;
  display: inline-flex;
  font-weight: 600;
  justify-content: center;
  min-height: 2.75rem;
  padding: 0 1.25rem;
}

.terms-action--primary {
  background: #29b765;
  border: 1px solid #29b765;
  color: #fff;
}

.terms-action--primary:hover:not([disabled]) {
  background: #229b55;
  border-color: #229b55;
  color: #fff;
}

.terms-action--primary[disabled] {
  background: #e4e7e9;
  border-color: #e4e7e9;
  color: #8a9094;
  cursor: not-allowed;
  opacity: 1;
}

.terms-action--secondary {
  background: #fff;
  border: 1px solid #979ea2;
  color: #3d4145;
  margin-top: 1rem;
}

.terms-action--secondary:hover {
  background: #f7f8f8;
  border-color: #6e7377;
  color: #252729;
}

.terms-footer {
  align-items: center;
  color: #6e6e6e;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.75rem;
  gap: 0.75rem 1.5rem;
  justify-content: center;
  margin: 1rem auto 0;
  text-align: center;

  img {
    display: block;
  }
}

@media (max-width: 768px) {
  .terms-wrapper {
    padding: 0;
  }

  .terms-card {
    border-radius: 0;
    width: 100%;
  }

  .terms-card__content {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .terms-actions .button {
    flex: 1;
  }
}
</style>

<style lang="scss">
@media screen and (max-width: 768px) {
  #app main#main {
    height: 100%;
    margin-top: 0;
    margin-bottom: 0;
  }
}
</style>
